<template lang="pug">
.gym-details-drawer
  loading(v-if="isLoading")
  .not-found(v-else-if="!gym") {{ __('Unable to load gym data') }}
  .gym-details-container(v-else)
    StaticImage.gym-logo(v-if="showLogo" :src="logo?.src" :alt="gym.name" :autoHeight="true")
    .home-gym-banner(v-else-if="gym.type === 'Home Gym'")
      Icon.badge(name="member-badge-white")
      h2 {{ __('Rogue Equipped Home Gym')}}
    .gym-header
      h4.gym-title
        | {{gym.name}}
        Icon.featured-icon(v-if="gym.isFeatured" name="map/shield")
      .gym-type {{ (gym.type === 'Other' && gym.customType) ? gym.customType : __(gym.type)}}
      .allow-dropins(v-if="gym.allowDropIn && gym.type !== 'Home Gym'") {{ __('Drop-Ins Accepted') }}
    Tabs.gym-tabs(v-model="activeTab" :tabs="tabNames")
    overview-tab(v-if="activeTab === 'Overview'" :gym="gym" @featureProduct="$emit('featureProduct', $event)")
    equipment-tab(v-else-if="activeTab === 'Equipment'" :gym="gym" @featureProduct="$emit('featureProduct', $event)")
    gallery-tab(v-else-if="activeTab === 'Gallery'" :gym="gym")
</template>

<script setup lang="ts">
import { computed, onMounted, onServerPrefetch, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { IGymMedia, IGym, IGymProduct } from '@rogue/universal'
import OverviewTab from './OverviewTab.vue'
import EquipmentTab from './EquipmentTab.vue'
import GalleryTab from './GalleryTab.vue'
import { useRogue } from '@/js/services/Rogue'
import { useGymStore } from '@/js/stores/gymStore'

const route = useRoute()
const rogue = useRogue()
const gymStore = useGymStore()

const gym = ref<IGym | undefined>(undefined)
const isLoading = ref(true)

const tabNames = ref<string[]>(['Overview'])
const activeTab = ref<string>(tabNames.value[0])

const logo = computed<IGymMedia | undefined>(() => gym.value?.logo || gym.value?.media?.find(m => m.type === 'image'))
const showLogo = computed<boolean>(() => !!logo.value?.src)

async function loadGymDetails() {
  const gymId = route.query.gym
  gym.value = await gymStore.loadGymDetails(gymId as string, rogue)
  isLoading.value = false

  if (gym.value?.products?.length) {
    tabNames.value.push('Equipment')
  }
  if (gym.value?.media?.length) {
    tabNames.value.push('Gallery')
  }
}

onMounted(() => {
  loadGymDetails()
})

onServerPrefetch(async () => {
  await loadGymDetails()
})

defineEmits<{ (e: 'featureProduct', product: IGymProduct): void }>()

</script>

<style lang="scss" scoped>
.gym-details-drawer {
  .home-gym-banner {
    align-items: center;
    aspect-ratio: 12 / 7;
    background-color: $color-black;
    color: $color-white;
    display: flex;
    flex-direction: row;
    gap: $space-m;
    padding: 0 $space-xxxl;
    width: 100%;

    .badge {
      width: 86px;
      height: 88px;
      flex-shrink: 0;
    }

    h2 {
      font-size: $size-xxl;
      margin: 0;
    }
  }

  .gym-logo {
    width: 100%;
    height: auto;
    max-height: 240px;
  }
  .gym-header {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
    padding: $space-s;
    padding-bottom: 0;

    .gym-title {
      align-items: center;
      display: flex;
      flex-direction: row;
      font-size: $size-m;
      gap: $space-xxs;
      margin: 0;

      .featured-icon {
        height: $size-m;
        width: $size-s;
      }
    }

    .gym-type {
      font-size: $size-xs;
      line-height: $size-xs;
    }

    .allow-dropins {
      background-color: $color-black;
      color: $color-white;
      font-size: $size-xxs;
      font-weight: bold;
      line-height: $size-xxs;
      padding: $space-xxxs $space-xxs;
      width: fit-content;
    }
  }

  .gym-tabs .tab-header {
    margin-bottom: 0;
  }
}
</style>
<style lang="scss">
.gym-details-drawer .gym-tabs .tab-header {
  margin-bottom: 0;
}
</style>
